import { gql, useQuery } from '@apollo/client';
import type { Account } from '@interfaces/account';
import { AccountStates } from '@interfaces/account';
import {
  Card,
  CardContent,
  FormControlLabel,
  FormGroup,
  ListItem,
  Menu,
  Pagination,
} from '@mui/material';
import { legacyColors } from '@ovComponents/legacyColor';
import { useClientContext } from '@pages/client/clientContext';
import { useHouseholdContext } from '@pages/household/householdContext';
import { ObjectType } from '@providers/statsHooks';
import { usePermission } from '@providers/userContextProvider';
import { generateClientNameString } from '@utils/generateClientNameString';
import { STATE_CHANGES_FIELD } from '@utils/reusableGraphQLFields';
import { usePageState } from '@utils/usePageState';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from 'hephaestus/web';
import { Tune } from 'hephaestus/webIcons';
import { uniq } from 'lodash-es';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountItem from './accountItem';

const PAGE_SIZE = 10;

export const FETCH_ACCOUNTS = ({
  canReadAccountNumber,
  canReadClientLowRiskPii,
}: {
  canReadAccountNumber: boolean;
  canReadClientLowRiskPii: boolean;
}) => gql`
  query fetchAccounts($input: FetchAccountsInput!) {
    fetchAccounts(input: $input) {
      totalCount
      accounts {
        id: stringId
        type
        state
        ${STATE_CHANGES_FIELD}
        tlhEnabled
        ${canReadAccountNumber ? 'custodianAccountNumber' : ''}
        statistics {
          marketValueCents
          simpleReturnPercent
          simpleReturnAmount
        }
        subAccounts {
          id
          state
        }
        incompleteFormAgreements { id }
        user {
          id: stringId
          ${canReadClientLowRiskPii ? 'firstName' : ''}
          ${canReadClientLowRiskPii ? 'entityName' : ''}
        }
        householdClientGroup {
          id: stringId name
        }
      }
    }
  }
`;

const AccountsSummary = ({
  objectType,
  objectId,
  newAccountComponent,
}: {
  objectType: ObjectType.USER | ObjectType.CLIENT_GROUP;
  objectId: string;
  newAccountComponent?: ReactElement;
}) => {
  const { t } = useTranslation(['client']);
  const canReadAccountNumber = usePermission('read:account_number');
  const canReadClientLowRiskPii = usePermission('read:client_low_risk_pii');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = usePageState(1, 'accountsPage');
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [filterByState, setFilterByState] = useState<Record<string, boolean>>({
    [AccountStates.ACTIVE]: true,
    [AccountStates.REQUESTED]: true,
    [AccountStates.FROZEN]: true,
    [AccountStates.FAILED]: true,
    [AccountStates.INITIATED]: true,
    [AccountStates.READY]: true,
    [AccountStates.INACTIVE]: false,
    [AccountStates.CANCELED]: false,
  });
  const clientContext = useClientContext();
  const householdContext = useHouseholdContext();

  const { loading } = useQuery(
    FETCH_ACCOUNTS({ canReadAccountNumber, canReadClientLowRiskPii }),
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        input: {
          filter: {
            ...(objectType === ObjectType.USER && { userId: objectId }),
            ...(objectType === ObjectType.CLIENT_GROUP && {
              clientGroupId: objectId,
            }),
            states: Object.keys(filterByState).filter(
              (key) => filterByState[key],
            ),
          },
          pagination: {
            perPage: 1000,
          },
        },
      },
      onCompleted: (e) => {
        setAccounts([...e.fetchAccounts.accounts]);
        setTotalCount(e.fetchAccounts.totalCount);
      },
    },
  );

  const accountsSorted = accounts.sort(
    (a, b) =>
      (b.statistics?.marketValueCents ?? 0) -
      (a.statistics?.marketValueCents ?? 0),
  );
  const accountsPaginated = accountsSorted.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [filterByState]);

  const householdLinkedAccounts = accounts.filter(
    (a) => a.householdClientGroup,
  );
  const householdIds = uniq(
    householdLinkedAccounts.map((a) => a.householdClientGroup?.id),
  );
  const indexedHouseholdIds = Object.fromEntries(
    householdIds.map((id, index) => [
      id,
      index === 0 ? 'HOUSEHOLD' : index + 1,
    ]),
  );

  const getTag = (account: Account) => {
    if (clientContext) {
      if (!account.householdClientGroup) return undefined;

      return {
        sequenceNumber: indexedHouseholdIds[account.householdClientGroup.id],
        text: `${account.householdClientGroup.name} ${t('shared:household')}`,
      };
    }

    if (householdContext) {
      return {
        sequenceNumber: householdContext.indexedMembers[account.user.id],
        text: generateClientNameString(account.user) ?? account.user.id,
      };
    }

    return undefined;
  };

  const AccountsFilterMenu = () => (
    <ListItem>
      <Stack>
        <Typography
          variant='bodyLarge'
          sx={{
            fontWeight: 600,
            fontSize: '20px',
          }}
        >
          {t('accountsSummary.accountStateLabel')}
        </Typography>
        {Object.values(AccountStates).map((state, index) => (
          <FormGroup key={index}>
            <FormControlLabel
              control={<Checkbox checked={filterByState[state]} />}
              onClick={() =>
                setFilterByState({
                  ...filterByState,
                  [state]: !filterByState[state],
                })
              }
              label={t(`accountsDetail:accountState.${state}`)}
              sx={{ ml: 0, gap: 1 }}
            />
          </FormGroup>
        ))}
      </Stack>
    </ListItem>
  );

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '9px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant='bodyLarge'
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                }}
              >
                {t('accountsSummary.header')}
              </Typography>
            </Box>
            <div>
              <IconButton
                aria-label='Filter accounts'
                style={{
                  backgroundColor:
                    filterMenuAnchorEl && legacyColors.neutral300,
                }}
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  setFilterMenuAnchorEl(
                    filterMenuAnchorEl ? null : e.currentTarget,
                  )
                }
              >
                <Tune fontSize='inherit' />
              </IconButton>
              <Menu
                open={!!filterMenuAnchorEl}
                anchorEl={filterMenuAnchorEl}
                onClose={() => setFilterMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <AccountsFilterMenu />
              </Menu>
              {newAccountComponent}
            </div>
          </Box>

          {loading ? (
            <CircularProgress />
          ) : (
            accountsPaginated.map((account) => (
              <AccountItem
                key={account.id}
                account={account}
                tag={getTag(account)}
              />
            ))
          )}

          {totalCount > PAGE_SIZE ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'end',
              }}
            >
              <Pagination
                count={Math.ceil(totalCount / PAGE_SIZE)}
                sx={{ marginBottom: '10px' }}
                page={page}
                onChange={(_e, newPage) => {
                  setPage(newPage);
                }}
              />
            </Box>
          ) : undefined}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AccountsSummary;
